import type {ReactElement} from 'react';
import React from 'react';
import {ImageAndVideoToggle} from '@Panels/record-image-and-video-panel/components/image-and-video-toggle';
import {CameraDeviceToggle} from '@Panels/record-image-and-video-panel/components/camera-device-toggle';
import {ImageAndVideoRecorderStatus} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import {ShutterControlButton} from '@Panels/record-image-and-video-panel/components/shutter-control-button';
import {StopVideoRecordingButton} from '@Panels/record-image-and-video-panel/components/stop-video-recording-button';
import {useAppSelector} from '@/hooks';
import styles from './image-and-video-recorder-controls.module.scss';

interface ImageAndVideoRecorderControlsProps {
  duration: number;
  isOnMobile?: boolean;
  getCamera(): Promise<void>;
  captureImage(): void;
  startRecordingVideo(): void;
  stopRecordingVideo(): void;
  videoInputDevices: Array<string>;
  hideToggle?: boolean;
}

export function ImageAndVideoRecorderControls({
  isOnMobile = false,
  getCamera = (): Promise<void> => {
    return Promise.resolve();
  },
  captureImage = (): void => {},
  startRecordingVideo = (): void => {},
  stopRecordingVideo = (): void => {},
  hideToggle = false,
  ...props
}: ImageAndVideoRecorderControlsProps): ReactElement | null {
  const recorderStatus = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.imageAndVideoRecorderStatus;
  });

  const getControlButton = (): ReactElement | null => {
    if (recorderStatus === ImageAndVideoRecorderStatus.RECORDING) {
      return <StopVideoRecordingButton isOnMobile={isOnMobile} duration={props.duration} stopRecordingVideo={stopRecordingVideo} />;
    }
    if (recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD) {
      return <ShutterControlButton isOnMobile={isOnMobile} captureImage={captureImage} startRecordingVideo={startRecordingVideo} />;
    }
    return null;
  };

  const getVideoRecorderControls = (): ReactElement => {
    if (isOnMobile) {
      return (
        <div
          className={`${
            recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD ? styles.mobileControls : styles.mobileControlsRecording
          } spacing-m-r-4 flex-row-justify-between flex-items-center`}
        >
          {getControlButton()}
          <CameraDeviceToggle videoInputDevices={props.videoInputDevices} getCamera={getCamera} />
        </div>
      );
    }
    return (
      <div className="flex-row-justify-between flex-items-center">
        {!isOnMobile ? <ImageAndVideoToggle className={hideToggle ? '_invisible' : ''} /> : null}
        {getControlButton()}
        <CameraDeviceToggle videoInputDevices={props.videoInputDevices} getCamera={getCamera} />
      </div>
    );
  };

  const getRecorderControls = (): ReactElement | null => {
    return getVideoRecorderControls();
  };

  return <div>{getRecorderControls()}</div>;
}
